import { Breakpoint, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { Form, IFormProps } from './form';
import { useConfirm, useScreenSize } from '@dw/hooks';

export interface IModalPropsBase {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface IModalProps<TFormType = any> extends IModalPropsBase {
  children: React.ReactNode;
  formWrapper?: IFormProps<TFormType>
  actions?: React.ReactNode;
  maxWidth?: Breakpoint | false
  contentTopPadding?: string;
  confirmBackdropClose?: boolean;
  /**
   * Take the whole screen on a phone instead of floating in the middle of it.
   *
   * OPT-IN for now, and the honest reason is scope rather than taste: most of the ~38 dialogs using
   * this component carry a `formWrapper`, and Material's own guidance is full-screen on xs for
   * exactly those — so most of them probably SHOULD have it. Turning it on for all of them inside a
   * feature branch would be an unreviewable portal-wide visual change.
   *
   * The rule itself lives here, not at the call sites, so callers pass a boolean rather than
   * restating a breakpoint. Defaulting it (to `!!formWrapper`, say) stays a one-line change in one
   * file — which is what makes opt-in a staging post rather than a bandaid.
   */
  fullScreenOnMobile?: boolean;
  /**
   * Grow to `maxWidth` rather than to the content.
   *
   * Without it a dialog is only as wide as whatever happens to be inside it, so a form of short
   * inputs sits in a narrow column with `maxWidth` doing nothing at all.
   */
  fullWidth?: boolean;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function Modal<TFormType = any>(props: IModalProps<TFormType>) {

  const {
    title,
    open,
    onClose,
    children,
    actions,
    formWrapper,
    maxWidth,
    contentTopPadding,
    confirmBackdropClose,
    fullScreenOnMobile,
    fullWidth
  } = props;

  const { confirm } = useConfirm();
  const { isMobile } = useScreenSize();

  const closeDialog = (isBackdropClick: boolean) => {
    if (!confirmBackdropClose || !isBackdropClick) {
      onClose();
    } else {
      confirm({
        title: 'Discard Changes?',
        message: 'Are you sure you want to discard your changes?',
        positiveText: 'Discard Changes',
        onPositive: () => {
          onClose();
        }
      })
    }
  }

  const dialogBody = (
    <>
      {title && (
        <DialogTitle>{title}</DialogTitle>
      )}
      <DialogContent sx={{
        padding: isMobile ? '18px 15px' : undefined,
        paddingTop: contentTopPadding ? `${contentTopPadding} !important` : undefined
      }}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions>
          {actions}
        </DialogActions>
      )}
    </>
  )

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeDialog(reason === 'backdropClick' || reason === 'escapeKeyDown')}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      // `isMobile` is xs only (under 600px). A tablet keeps the floating dialog, which is the right
      // answer there — it has the room, and a full-screen sheet on a tablet loses the context behind
      // it for nothing.
      fullScreen={!!fullScreenOnMobile && isMobile}
    >
      {formWrapper ? (
        <Form {...formWrapper}>
          {dialogBody}
        </Form>
      ) : dialogBody}
    </Dialog>
  )
}